import {
  Button,
  Center,
  Drawer,
  DrawerCloseButton,
  DrawerContent,
  DrawerOverlay,
  Flex,
  Spacer,
  Text,
  useMediaQuery,
  VStack,
} from '@chakra-ui/react'
import { FC, MouseEventHandler } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { WhiteLogo } from './Logo'
import { UserSummaryPanel } from '../UserSummaryPanel'
import { LocationSwitcher } from './LocationSwitcher'
import { DESKTOP_BREAKPOINT } from '../../constants'
import { useLogout } from '../../hooks/useLogout'
import { useAppSelector } from '../../app/typedReduxHooks'
import { Permission } from '../../types/permission'
import { ExternalLinkIcon } from '@chakra-ui/icons'
import { useHasEntitlement } from '../../pages/OrganizationProfilePage/planEntitlements'
import { FeatureKey } from '../../types'

interface SideNavDrawerProps {
  isOpen: boolean
  onClose: () => void
}

interface NavProps {
  isSmallScreenNavOpen: boolean
  onCloseSmallScreenNav: () => void
}

interface SideNavLinkProps {
  to: string
  title: string
  onClick?: MouseEventHandler<HTMLButtonElement>
}

const SideNavLink: FC<SideNavLinkProps> = ({ to, title, onClick }) => {
  const location = useLocation()

  return (
    <Button
      justifyContent="flex-start"
      colorScheme="blue"
      bgColor={location.pathname === to ? 'blue.200' : 'blue.800'}
      as={Link}
      to={to}
      onClick={onClick}
    >
      {title}
    </Button>
  )
}

const SideNav: FC<{ isNavDrawer?: boolean }> = ({ isNavDrawer }) => {
  const logout = useLogout()
  const permissions = useAppSelector((state) => state.authentication.permissions)
  const prescriber = useAppSelector((state) => state.prescriber?.prescriber)

  const organizationSettings = useAppSelector((state) => state.organization?.data?.settings)
  const isEnableHelpDocument = organizationSettings?.enableHelpDocument
  const hasMultipleLocationsFeatureOn = useHasEntitlement(FeatureKey.MULTIPLE_LOCATIONS)

  return (
    <Flex flexDirection="column" bgColor="blue.800" p="3" h="100%">
      <VStack p={'6'}>
        <Center>
          <WhiteLogo maxWidth={150} />
        </Center>
      </VStack>
      {
        <VStack spacing={10} justifyContent="space-between">
          <VStack width={isNavDrawer ? '296px' : '260px'} spacing={2} alignItems="stretch">
            {prescriber && hasMultipleLocationsFeatureOn && <LocationSwitcher />}
            {permissions?.includes(Permission.PRESCRIBING) && (
              <SideNavLink to="/new-script" title="New Prescription" />
            )}
            {(prescriber || permissions?.includes(Permission.PATIENT_MANAGEMENT)) && (
              <SideNavLink to="/patients" title="Manage Patients" />
            )}
            {(prescriber || permissions?.includes(Permission.PRESCRIPTION_MANAGEMENT)) && (
              <SideNavLink to="/prescriptions" title="Manage Prescriptions" />
            )}
          </VStack>
        </VStack>
      }
      <Spacer />
      <VStack width="260px" spacing={10} alignItems="stretch" justifyContent="space-between">
        <VStack width="260px" spacing={2} alignItems="stretch">
          <Text
            fontSize="xs"
            fontWeight="bold"
            color="whiteAlpha.600"
            mb={2}
            letterSpacing="wider"
            pt={4}
          >
            SUPPORT
          </Text>

          {permissions && permissions.length > 0 && <SideNavLink to="/settings" title="Settings" />}
          {isEnableHelpDocument ? (
            <Button
              justifyContent="flex-start"
              colorScheme="blue"
              bgColor={'blue.800'}
              as="a"
              href="https://help.rxpad.com.au"
              target="_blank"
              rightIcon={<ExternalLinkIcon />}
            >
              Help
            </Button>
          ) : null}

          <SideNavLink
            to="/"
            title="Logout"
            onClick={(e) => {
              e.preventDefault()
              logout()
            }}
          />
        </VStack>
        <UserSummaryPanel />
      </VStack>
    </Flex>
  )
}

export const SideNavDrawer: FC<Required<SideNavDrawerProps>> = ({ isOpen, onClose }) => (
  <Drawer isOpen={isOpen} placement="left" onClose={onClose} size={'xs'}>
    <DrawerOverlay />
    <DrawerContent>
      <DrawerCloseButton color="white" />
      <SideNav isNavDrawer />
    </DrawerContent>
  </Drawer>
)

const Nav: FC<NavProps> = ({ isSmallScreenNavOpen, onCloseSmallScreenNav }) => {
  const [isDesktop] = useMediaQuery(`(min-width: ${DESKTOP_BREAKPOINT})`)
  return isDesktop ? (
    <SideNav />
  ) : (
    <SideNavDrawer isOpen={isSmallScreenNavOpen} onClose={onCloseSmallScreenNav} />
  )
}

export default Nav
